import useDownloadTicket from '@/hooks/useDownloadTicket';
import { TransText } from '@/i18n/trans/text';
import { breakpoints } from '@/utils/breakpoints';
import { Button, Icons, Popover, Sheet } from '@packages/webshop-design-system';
import type { FC } from 'react';
import { useMediaQuery } from 'react-responsive';

interface TravelPassDownloadProps {
  fulfillmentId: string | undefined;
  bookingId: string | undefined;
  bookingCode: string | undefined;
}

const TravelPassDownload: FC<TravelPassDownloadProps> = ({
  fulfillmentId,
  bookingId,
  bookingCode,
}) => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const {
    isDownloading,
    isPkPassDownloading,
    handleTicketDownload,
    handlePkPassDownload,
  } = useDownloadTicket({
    bookingId,
    code: bookingCode,
    payerEmail: undefined,
    fulfillments: fulfillmentId ? [{ id: fulfillmentId }] : [],
  });

  function renderDownloadButton() {
    return (
      <Button
        data-testid="travel-pass-download"
        variant="secondary"
        size={isLaptopOrBigger ? 'regular' : 'large'}
        className="group gap-1.5"
      >
        <Icons.download width={20} height={20} />
        <TransText i18nKey="download" />
        <Icons.chevronDown
          height={20}
          width={20}
          className="transition-transform duration-200 group-aria-expanded:rotate-180"
        />
      </Button>
    );
  }

  function renderDownloadContent() {
    return (
      <div className="flex flex-col gap-2 p-3 laptop:p-0">
        <Button
          data-testid="travel-pass-download-pdf"
          variant="secondary"
          size={isLaptopOrBigger ? 'regular' : 'large'}
          isDisabled={isDownloading}
          onClick={handleTicketDownload}
        >
          {isDownloading ? (
            <Icons.loader height={16} width={16} className="animate-spin" />
          ) : (
            <Icons.download height={16} width={16} />
          )}
          <TransText i18nKey="download" />
        </Button>
        <Button
          data-testid="travel-pass-add-to-wallet"
          variant="secondary"
          size={isLaptopOrBigger ? 'regular' : 'large'}
          isDisabled={isPkPassDownloading}
          onClick={handlePkPassDownload}
        >
          {isPkPassDownloading ? (
            <Icons.loader height={16} width={16} className="animate-spin" />
          ) : (
            <Icons.pkPassWallet height={16} width={16} />
          )}
          <TransText i18nKey="addToWallet" />
        </Button>
      </div>
    );
  }

  if (isLaptopOrBigger) {
    return (
      <Popover
        trigger={renderDownloadButton()}
        content={renderDownloadContent()}
        data-testid="travel-pass-download-popover"
        className={{ content: 'flex flex-col gap-2 p-4' }}
      />
    );
  }

  return (
    <Sheet
      trigger={renderDownloadButton()}
      side="bottom"
      headerIcon={
        <Icons.download width={20} height={20} className="text-primary" />
      }
      headerTitle={<TransText i18nKey="download" />}
      className={{ content: 'p-0' }}
      data-testid="travel-pass-download-sheet"
    >
      {renderDownloadContent()}
    </Sheet>
  );
};

export default TravelPassDownload;
